import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Mail, Lock, Truck, RotateCcw, ShieldCheck, Heart,
  Eye, EyeOff, User, Phone, Globe, KeyRound, ArrowLeft, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NATIONALITIES = [
  { code: 'IN', label: 'India (₹ INR)' },
  { code: 'US', label: 'United States ($ USD)' },
  { code: 'GB', label: 'United Kingdom ($ USD)' },
  { code: 'CA', label: 'Canada ($ USD)' },
  { code: 'AU', label: 'Australia ($ USD)' },
  { code: 'AE', label: 'United Arab Emirates ($ USD)' },
  { code: 'SG', label: 'Singapore ($ USD)' },
  { code: 'DE', label: 'Germany ($ USD)' },
  { code: 'FR', label: 'France ($ USD)' },
  { code: 'OTHER', label: 'Other ($ USD)' },
];

const PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const passwordChecks = (pw) => ({
  length: (pw || '').length >= 8,
  upper:  /[A-Z]/.test(pw || ''),
  lower:  /[a-z]/.test(pw || ''),
  number: /\d/.test(pw || ''),
  symbol: /[^A-Za-z0-9]/.test(pw || ''),
});

const PasswordHints = ({ password }) => {
  const c = passwordChecks(password);
  const Row = ({ ok, children }) => (
    <li className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-600' : 'text-slate-400'}`}>
      <CheckCircle2 className={`w-3.5 h-3.5 ${ok ? 'text-emerald-500' : 'text-slate-300'}`} />
      {children}
    </li>
  );
  return (
    <ul className="grid grid-cols-2 gap-y-1 mt-2" data-testid="password-hints">
      <Row ok={c.length}>8+ characters</Row>
      <Row ok={c.upper}>1 uppercase</Row>
      <Row ok={c.lower}>1 lowercase</Row>
      <Row ok={c.number}>1 number</Row>
      <Row ok={c.symbol}>1 special char</Row>
    </ul>
  );
};

const Login = () => {
  useEffect(() => {
    const n = sessionStorage.getItem('login_notice');
    if (n) { sessionStorage.removeItem('login_notice'); toast.info(n); }
  }, []);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Login/Signup tabs no longer need OTP. Forgot-password keeps OTP.
  // forgotStage: null | 'email' | 'reset'
  const [activeTab, setActiveTab] = useState('login');
  const [forgotStage, setForgotStage] = useState(null);
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [signupData, setSignupData] = useState({
    name: '', email: '', password: '', phone: '', nationality: 'IN',
  });
  const [signupError, setSignupError] = useState('');
  const [resetEmail, setResetEmail] = useState('');

  const handleLoginChange = (e) => {
    if (loginError) setLoginError('');
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };
  const handleSignupChange = (e) => {
    if (signupError) setSignupError('');
    setSignupData({ ...signupData, [e.target.name]: e.target.value });
  };

  const persistSession = (data) => {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    sessionStorage.removeItem('admissionPopupShown');
    window.dispatchEvent(new Event('auth-changed'));
  };

  // ---------- Login ----------
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/auth/login`, loginData);
      persistSession(r.data);
      toast.success(`Welcome back, ${r.data.user.name}!`);
      navigate(new URLSearchParams(window.location.search).get('next') || '/account');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid email or password';
      setLoginError(detail);
      toast.error('Login Failed', { description: detail });
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Signup ----------
  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError('');
    if (!PASSWORD_RE.test(signupData.password)) {
      const msg = 'Use 8+ characters with upper, lower, number and a special character.';
      setSignupError(msg);
      toast.error('Weak password', { description: msg });
      return;
    }
    setIsLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/auth/signup`, signupData);
      persistSession(r.data);
      toast.success(`Welcome to Scholar Industry, ${r.data.user.name}!`);
      navigate(new URLSearchParams(window.location.search).get('next') || '/account');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not create account';
      setSignupError(detail);
      toast.error('Signup failed', { description: detail });
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Forgot password ----------
  const handleForgotRequest = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/send-otp`, {
        email: resetEmail, purpose: 'reset',
      });
      toast.success('Reset code sent', {
        description: `Check ${resetEmail} for the 6-digit code.`,
      });
      setResetOtp('');
      setNewPassword('');
      setForgotStage('reset');
    } catch (err) {
      toast.error('Could not send reset code', {
        description: err.response?.data?.detail || 'No account found with that email.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!PASSWORD_RE.test(newPassword)) {
      toast.error('Weak password', {
        description: 'Use 8+ characters with upper, lower, number and a special character.',
      });
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        email: resetEmail,
        otp: resetOtp.trim(),
        new_password: newPassword,
      });
      toast.success('Password reset!', { description: 'You can now sign in with your new password.' });
      setLoginData({ email: resetEmail, password: '' });
      setForgotStage(null);
      setActiveTab('login');
    } catch (err) {
      toast.error('Reset failed', {
        description: err.response?.data?.detail || 'Invalid or expired code.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const BackButton = ({ onClick }) => (
    <button type="button" onClick={onClick} data-testid="back-button"
      className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#1F4FB8] mb-4">
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
  );

  return (
    <div className="min-h-screen flex pb-16" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="hidden lg:flex lg:w-1/2 bg-[#0B1F4B] flex-col justify-center p-12" data-testid="login-brand-panel">
        <div className="bg-white rounded-lg p-6 w-fit"><img src="/scholar-logo-mark.png" alt="Scholar Industry" className="h-24 w-auto" /><p className="mt-2 text-lg font-bold italic text-[#0B1F4B]">Every Design Has a Story for You</p></div>
        <h2 className="mt-10 text-4xl font-bold text-white leading-tight max-w-md">Every design has a story for you.</h2>
        <ul className="mt-8 space-y-5 max-w-md">
          {[[Truck, 'Free delivery all over India on ₹799+'], [RotateCcw, '7-day no-questions replacement'], [ShieldCheck, 'Secure payments via Razorpay'], [Heart, 'Save favourites to your wishlist']].map(([Icon, t]) => (
            <li key={t} className="flex items-center gap-4 text-white text-lg"><span className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center"><Icon className="w-5 h-5 text-[#FF9F00]" /></span>{t}</li>
          ))}
        </ul>
      </div>

      {/* Right side — form */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-8 bg-[#F1F3F6]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" data-testid="login-logo"><img src="/scholar-logo-mark.png" alt="Scholar Industry" className="h-20 w-auto mx-auto" /><p className="mt-1 text-base font-bold italic text-[#0B1F4B]">Every Design Has a Story for You</p></Link>
          </div>

          <div className="bg-white rounded-lg border border-[#E5E7EB] shadow-sm p-6 md:p-10">
            {forgotStage === null ? (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8">
                  <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
                  <TabsTrigger value="signup" data-testid="tab-signup">Sign Up</TabsTrigger>
                </TabsList>

                {/* LOGIN */}
                <TabsContent value="login">
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
                    <p className="text-gray-600">Sign in to your Scholar Industry account</p>
                  </div>
                  <form onSubmit={handleLogin} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          id="login-email" data-testid="login-email-input"
                          name="email" type="email" required
                          value={loginData.email} onChange={handleLoginChange}
                          placeholder="you@example.com" className="pl-10 h-12 rounded-xl"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          id="login-password" data-testid="login-password-input"
                          name="password" type={showPassword ? 'text' : 'password'} required
                          value={loginData.password} onChange={handleLoginChange}
                          placeholder="••••••••" className="pl-10 pr-10 h-12 rounded-xl"
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <div className="text-right -mt-2">
                      <button type="button" data-testid="forgot-password-link"
                        onClick={() => { setResetEmail(loginData.email); setForgotStage('email'); }}
                        className="text-sm text-[#1F4FB8] hover:underline font-medium">
                        Forgot password?
                      </button>
                    </div>

                    {loginError && (
                      <div
                        data-testid="login-error-banner"
                        role="alert"
                        className="flex items-start gap-2 rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3"
                      >
                        <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" />
                        </svg>
                        <div className="text-sm text-red-700 font-semibold leading-relaxed">
                          {loginError === 'Invalid email or password'
                            ? 'Wrong email or password — please try again.'
                            : loginError}
                        </div>
                      </div>
                    )}

                    <Button type="submit" disabled={isLoading}
                      data-testid="login-submit-btn"
                      className="w-full h-12 bg-[#0B1F4B] hover:bg-[#16316F] text-white font-bold rounded-md">
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </TabsContent>

                {/* SIGNUP */}
                <TabsContent value="signup">
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
                    <p className="text-gray-600">Join Scholar Industry today</p>
                  </div>
                  <form onSubmit={handleSignup} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input id="signup-name" data-testid="signup-name-input"
                          name="name" required value={signupData.name}
                          onChange={handleSignupChange}
                          placeholder="John Doe" className="pl-10 h-12 rounded-xl" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input id="signup-email" data-testid="signup-email-input"
                          name="email" type="email" required value={signupData.email}
                          onChange={handleSignupChange}
                          placeholder="you@example.com" className="pl-10 h-12 rounded-xl" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-phone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input id="signup-phone" data-testid="signup-phone-input"
                          name="phone" type="tel" value={signupData.phone}
                          onChange={handleSignupChange}
                          placeholder="+91 98765 43210" className="pl-10 h-12 rounded-xl" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-nationality">Nationality</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                        <select id="signup-nationality" data-testid="signup-nationality-select"
                          name="nationality" value={signupData.nationality}
                          onChange={handleSignupChange} required
                          className="w-full pl-10 pr-3 h-12 rounded-xl border border-input bg-background text-sm">
                          {NATIONALITIES.map((n) => (
                            <option key={n.code} value={n.code}>{n.label}</option>
                          ))}
                        </select>
                      </div>
                                          </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input id="signup-password" data-testid="signup-password-input"
                          name="password" type={showPassword ? 'text' : 'password'} required
                          value={signupData.password} onChange={handleSignupChange}
                          placeholder="At least 8 characters" minLength={8}
                          className="pl-10 pr-10 h-12 rounded-xl" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <PasswordHints password={signupData.password} />
                    </div>

                    {signupError && (
                      <div
                        data-testid="signup-error-banner"
                        role="alert"
                        className="flex items-start gap-2 rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3"
                      >
                        <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" />
                        </svg>
                        <div className="text-sm text-red-700 font-semibold leading-relaxed">
                          {signupError}
                        </div>
                      </div>
                    )}

                    <Button type="submit" disabled={isLoading}
                      data-testid="signup-submit-btn"
                      className="w-full h-12 bg-[#0B1F4B] hover:bg-[#16316F] text-white font-bold rounded-md">
                      {isLoading ? 'Creating account...' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            ) : forgotStage === 'email' ? (
              <>
                <BackButton onClick={() => setForgotStage(null)} />
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h1>
                  <p className="text-gray-600">Enter your email and we'll send a reset code.</p>
                </div>
                <form onSubmit={handleForgotRequest} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input id="reset-email" data-testid="forgot-email-input"
                        type="email" required value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="you@example.com" className="pl-10 h-12 rounded-xl" />
                    </div>
                  </div>
                  <Button type="submit" disabled={isLoading}
                    data-testid="forgot-send-otp-btn"
                    className="w-full h-12 bg-[#0B1F4B] hover:bg-[#16316F] text-white font-bold rounded-md">
                    {isLoading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <BackButton onClick={() => setForgotStage('email')} />
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">New Password</h1>
                  <p className="text-gray-600">
                    Enter the code we sent to <span className="font-semibold">{resetEmail}</span> and choose a new password.
                  </p>
                </div>
                <form onSubmit={handleResetSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="reset-otp">6-Digit Verification Code</Label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input id="reset-otp" data-testid="reset-otp-input"
                        inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                        value={resetOtp}
                        onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, ''))}
                        required placeholder="123456"
                        className="pl-10 h-12 rounded-xl tracking-[0.5em] text-center font-semibold" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input id="new-password" data-testid="reset-new-password-input"
                        type={showPassword ? 'text' : 'password'} required
                        value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="At least 8 characters" minLength={8}
                        className="pl-10 pr-10 h-12 rounded-xl" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <PasswordHints password={newPassword} />
                  </div>
                  <Button type="submit" disabled={isLoading || resetOtp.length !== 6}
                    data-testid="reset-submit-btn"
                    className="w-full h-12 bg-[#0B1F4B] hover:bg-[#16316F] text-white font-bold rounded-md">
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </form>
              </>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">← Back to Home</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
